import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { FormFieldValueProps } from "./form-field/FormFieldProps";
import { MultiInputProps } from "./multi-input/MultiInput";

type StateLike<V> = [V, Dispatch<SetStateAction<V>>];

export function useFormFieldState<V>(props: FormFieldValueProps<V>, fallbackValue: V): StateLike<V> {
  const value = useDistinct(useMemo(
    () => props.value ?? props.defaultValue ?? fallbackValue,
    [fallbackValue, props.defaultValue, props.value],
  ));

  const onValue = props.onValue;
  const safeOnValue = useCallback(
    (v: V) => onValue
      ? onValue(v)
      : () => {
        },
    [onValue],
  );

  const dispatchValue = useCallback(
    (action: SetStateAction<V>) => {
      if (action instanceof Function) {
        safeOnValue(action(value));
      } else {
        safeOnValue(action);
      }
    },
    [safeOnValue, value],
  );

  return [value, dispatchValue];
}

export function useMultiInputState<V, T extends FormFieldValueProps<V>>(props: MultiInputProps<V, T>): StateLike<(V | undefined)[]> {
  const values = useDistinct(useMemo(
    () => props.values ?? [props.defaultValue],
    [props.defaultValue, props.values],
  ));

  const onValues = props.onValues;
  const safeOnValues = useCallback(
    (vs: (V | undefined)[]) => onValues
      ? onValues(vs)
      : () => {
        },
    [onValues],
  );

  const dispatchValues = useCallback(
    (action: SetStateAction<(V | undefined)[]>) => {
      if (action instanceof Function) {
        safeOnValues(action(values));
      } else {
        safeOnValues(action);
      }
    },
    [safeOnValues, values],
  );

  return [values, dispatchValues];
}

export function useDistinct<T>(value: T) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => value, [JSON.stringify(value)]);
}
